import React from 'react';
import { array, number, shape, string, oneOfType, instanceOf } from 'prop-types';
import classNames from 'classnames';
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min';
import Decimal from 'decimal.js';

import { FormattedMessage, useIntl } from '../../util/reactIntl';

import css from './BookingSummary.module.css';

const hourOf = m => (m.minutes() ? m.format('H:mm') : m.format('H'));

const lineItemQuantity = (lineItems, code) => {
  const item = lineItems.find(i => i.code === code && !i.reversal);
  return item?.quantity != null ? new Decimal(item.quantity).toNumber() : null;
};

const guestRangeLabel = ({ min, max }, intl) =>
  max >= 999
    ? intl.formatMessage({ id: 'CheckoutPage.priceBreakdownGuest51Above' })
    : intl.formatMessage({ id: 'CheckoutPage.priceBreakdownGuestBetween' }, { min, max });

// The server writes the booked tier as line items, so this works for any face.
const guestRangeFromLineItems = lineItems => {
  const min = lineItemQuantity(lineItems, 'line-item/guest-count-minimum');
  const max = lineItemQuantity(lineItems, 'line-item/guest-count-maximum');
  return min && max ? { min, max } : null;
};

/**
 * "Fecha" and "Grupo" of a booking, shown above the order breakdown (checkout
 * side card, transaction page for the bañista).
 *
 * `guestLabel` wins when the caller already has a better one (the checkout
 * reads it from the listing's tiers). `extraHours` and `groupOverride` reflect
 * upgrades (ampliaciones) on an accepted booking — see src/util/upgrades.js.
 */
const BookingSummary = props => {
  const {
    className,
    start,
    end,
    timeZone,
    lineItems,
    guestLabel: guestLabelProp,
    extraHours,
    groupOverride,
  } = props;
  const intl = useIntl();
  const tz = timeZone || 'Europe/Madrid';

  let dateLabel = null;
  if (start && end) {
    const mStart = moment(start).tz(tz);
    const mEnd = moment(end)
      .tz(tz)
      .add(extraHours || 0, 'hours');
    const formatDay = m =>
      intl.formatDate(m.toDate(), { day: 'numeric', month: 'long', timeZone: tz });
    dateLabel = mStart.isSame(mEnd, 'day')
      ? intl.formatMessage(
          { id: 'BookingSummary.dateHours' },
          { date: formatDay(mStart), start: hourOf(mStart), end: hourOf(mEnd) }
        )
      : intl.formatMessage(
          { id: 'BookingSummary.dateRange' },
          { start: formatDay(mStart), end: formatDay(mEnd) }
        );
  }

  const guestRange = groupOverride || guestRangeFromLineItems(lineItems || []);
  const guestLabelRaw =
    (!groupOverride && guestLabelProp) || (guestRange ? guestRangeLabel(guestRange, intl) : null);
  // The shared copy is lowercase ("entre 6 y 11 personas") because the price
  // modal uses it mid-sentence; here it stands on its own line.
  const guestLabel = guestLabelRaw
    ? guestLabelRaw.charAt(0).toUpperCase() + guestLabelRaw.slice(1)
    : null;

  if (!dateLabel && !guestLabel) {
    return null;
  }

  return (
    <dl className={classNames(css.root, className)}>
      {dateLabel ? (
        <div className={css.item}>
          <dt className={css.label}>
            <FormattedMessage id="BookingSummary.date" />
          </dt>
          <dd className={css.value}>{dateLabel}</dd>
        </div>
      ) : null}
      {guestLabel ? (
        <div className={css.item}>
          <dt className={css.label}>
            <FormattedMessage id="BookingSummary.group" />
          </dt>
          <dd className={css.value}>{guestLabel}</dd>
        </div>
      ) : null}
    </dl>
  );
};

const dateish = oneOfType([instanceOf(Date), string]);

BookingSummary.defaultProps = {
  className: null,
  start: null,
  end: null,
  timeZone: null,
  lineItems: null,
  guestLabel: null,
  extraHours: 0,
  groupOverride: null,
};

BookingSummary.propTypes = {
  className: string,
  start: dateish,
  end: dateish,
  timeZone: string,
  lineItems: array,
  guestLabel: string,
  extraHours: number,
  groupOverride: shape({ min: number, max: number }),
};

export default BookingSummary;
